
import {Component, Vue, Watch} from "vue-property-decorator";
import {State} from "vuex-class";
import * as moment from "moment";
import {Moment} from "moment";
import TextFilter from "./TextFilter.vue";
import SelectionFilter from "./SelectionFilter.vue";
import MultiSelectionFilter from "./MultiSelectionFilter.vue";
import DateFilter from "./DateFilter.vue";
import { FilterItem, FilterModel } from "./FilterModel";
import { IAllocations } from "@/contracts/AllocationContract";
import { IAccountCluster } from "@/contracts/AccountClusterContract";
import { eventService } from "@/shared/services/EventService";
import { ISkill } from "@/contracts/SkillContract";
import { ITribe } from "@/contracts/TribeContract";
import { IFilterState } from "@/store/contract";
import {IHive} from "@/contracts/HiveContract";
import {ICapability} from "@/contracts/CapabilityContract";
import {seniorityMap} from "@/shared/SeniorityMap";
import { TagOptions } from '@/data-management/tag-options';

@Component({
  components: {
    TextFilter,
    SelectionFilter,
    MultiSelectionFilter,
    DateFilter
  }
})
export default class AllFiltersModal extends Vue {
  @State("filter")
  filterState: IFilterState;

  private appliedFilter: FilterModel = new FilterModel();
  private currentFilter: FilterModel = new FilterModel();

  private consultantTypeOptions: FilterItem[] = [];
  private seniorityOptions: FilterItem[] = [];
  private skillOptions: FilterItem[] = [];
  private tribeOptions: FilterItem[] = [];
  private hiveOptions: FilterItem[] = [];
  private accountOptions: FilterItem[] = [];
  private accountClusterOptions: FilterItem[] = [];
  private peopleCoachesOptions: FilterItem[] = [];
  private profilesOptions: FilterItem[] = [];
  private quickFiltersOptions: FilterItem[] = [];

  created() {
    this.consultantTypeOptions = TagOptions.getConsultantTypeOptions();
    this.seniorityOptions = this.getSeniorityOptions();
    this.skillOptions = this.getSkillOptions();
    this.tribeOptions = this.getTribeOptions();
    this.hiveOptions = this.getHiveOptions();
    this.accountOptions = this.getAccountOptions();
    this.accountClusterOptions = this.getAccountClusterOptions();
    this.peopleCoachesOptions = TagOptions.getPeopleCoachesOptions();
    this.profilesOptions = this.getProfiles();
    this.quickFiltersOptions = TagOptions.getQuickFiltersOptions();
  }

  @Watch("filterState.filterModel", { immediate: true })
  onFilterModelChanged(newModel: FilterModel | null) {
    if (!newModel) {
      this.appliedFilter = new FilterModel();
      this.currentFilter = new FilterModel();
      return;
    }

    if (newModel === this.appliedFilter) {
      this.appliedFilter.copyTo(this.currentFilter);
      return;
    }

    newModel.copyTo(this.appliedFilter);
    newModel.copyTo(this.currentFilter);
  }

  private updateFirstName(newValue: string) {
    this.currentFilter.updateFirstName(newValue);
  }

  private updateLastName(newValue: string) {
    this.currentFilter.updateLastName(newValue);
  }

  private updateConsultantType(newItems: FilterItem[]) {
    this.currentFilter.updateConsultantType(newItems);
  }

  private updateSeniority(newItems: FilterItem[]) {
    this.currentFilter.updateSeniority(newItems);
  }

  private updateSkills(newItems: FilterItem[]) {
    this.currentFilter.updateSkills(newItems);
  }

  private updateTribes(newItems: FilterItem[]) {
    this.currentFilter.updateTribes(newItems);
  }

  private updateHives(newItems: FilterItem[]) {
    this.currentFilter.updateHives(newItems);
  }

  private updateAccountClusters(newItems: FilterItem[]) {
    this.currentFilter.updateAccountClusters(newItems);
  }

  private updateAccounts(newItems: FilterItem[]) {
    this.currentFilter.updateAccounts(newItems);
  }

  private updateStartDateFrom(newDate: Moment) {
    this.currentFilter.updateStartDateFrom(newDate);
  }

  private updateStartDateTo(newDate: Moment) {
    this.currentFilter.updateStartDateTo(newDate);
  }

  private updatePeopleCoaches(newItems: FilterItem[]) {
    this.currentFilter.updatePeopleCoaches(newItems);
  }

  private updateProfiles(newItems: FilterItem[]) {
    this.currentFilter.updateProfiles(newItems);
  }

  private updateQuickFilters(newItems: FilterItem[]) {
    this.currentFilter.updateQuickFilters(newItems);
  }

  private prefillNewHires() {
    const threeMonthsAgo = moment().subtract(3, "months");
    this.currentFilter.updateStartDateFrom(threeMonthsAgo);
    this.currentFilter.updateStartDateTo(null);
    this.applyFilter();
  }

  private clearFilter() {
    this.currentFilter.clear();
  }

  private closeFilter() {
    this.filterState.filterModalActive = false;
  }

  private applyFilter() {
    this.currentFilter.copyTo(this.appliedFilter);
    this.checkIfFiltersAreApplied();
    this.$store.state.commandManager.filterCommandExecutor.requestExecute({
      filter: this.appliedFilter
    });

    const modelForStore = new FilterModel();
    this.appliedFilter.copyTo(modelForStore);
    this.filterState.filterModel = modelForStore;
    this.trackGoogleAnalyticsEvent(this.appliedFilter);
    this.closeFilter();
  }

  private cancelFilter() {
    this.closeFilter();
    this.appliedFilter.copyTo(this.currentFilter);
  }

  private checkIfFiltersAreApplied() {
    this.filterState.filtersAreApplied = !this.appliedFilter.isEmpty();
  }


  private getSeniorityOptions(): FilterItem[] {
    return (this.$store.state.dataSourceLoader.employeesDataSource
      .functionSeniorShipCodes as string[])
      .map(el => {
        const seniorityMatch = seniorityMap[el] || { Name: el, Code: el, Order: 20 };
        return {
          label: seniorityMatch.Name,
          value: seniorityMatch.Code,
          order: seniorityMatch.Order
        };
      })
      .sort((s1, s2) => {
        // Sort by order first
        if (s1.order !== s2.order) {
          return s1.order - s2.order;
        }
        // If order is the same, sort alphabetically by label
        return s1.label.localeCompare(s2.label);
      });
  }

  private getTribeOptions(): FilterItem[] {
    const tribes = (this.$store.state.dataSourceLoader.tribeDataSource
      .data as ITribe[])
      .map(el => {
        return {
          label: el.Name,
          value: el.TribeId
        };
      })
      .sort((h1, h2) => h1.label.localeCompare(h2.label));
    return [{ label: "No Tribe", value: null }].concat(tribes);
  }

  private getHiveOptions(): FilterItem[] {
    const hives = (this.$store.state.dataSourceLoader.hiveDataSource
        .data as IHive[])
        .map(el => {
          return {
            label: el.Name,
            value: el.HiveId
          };
        })
        .sort((h1, h2) => h1.label.localeCompare(h2.label));
    return [{ label: "No Hive", value: null }].concat(hives);
  }

  private getSkillOptions(): FilterItem[] {
    return (this.$store.state.dataSourceLoader.skillsDataSource
      .data as ISkill[])
      .map(el => {
        return {
          label: el.Name,
          value: el.Id
        };
      })
      .sort((s1, s2) => s1.label.localeCompare(s2.label));
  }

  private getAccountOptions(): FilterItem[] {
    const accountIdToAllocation = this.$store.state.dataSourceLoader
      .employeesToAllocationsDataSource.accountIdToAllocation as {
      [accountId: string]: IAllocations;
    };
    return Object.keys(accountIdToAllocation)
      .map(accountId => {
        const account = accountIdToAllocation[accountId];
        return {
          label: account.AccountName,
          value: account.AccountId
        };
      })
      .sort((a1, a2) => a1.label.localeCompare(a2.label));
  }

  private getProfiles() {
    return (this.$store.state.dataSourceLoader.capabilitiesDataSource
        .data as ICapability[])
        .map(el => {
          return {
            label: el.label,
            value: el.code
          };
        })
        .sort((a1, a2) => a1.label.localeCompare(a2.label));
  }

  private getAccountClusterOptions(): FilterItem[] {
    const accountClusters = (this.$store.state.dataSourceLoader
      .accountClusterDataSource.data as IAccountCluster[])
      .map(el => {
        return { label: el.Name, value: el.ClusterId };
      })
      .sort((ac1, ac2) => ac1.label.localeCompare(ac2.label));
    return [{ label: "No Account Cluster", value: null }].concat(
      accountClusters
    );
  }

  private trackGoogleAnalyticsEvent(filter: FilterModel) {
    let categories: string[] = [];
    let values: string[] = [];
    Object.keys(filter).forEach(key => {
      const prop = filter[key];
      if (Array.isArray(prop)) {
        if (prop && prop.length) {
          let selectedValues = prop.map(p => p.label);
          eventService.track("Filter - Single", key, selectedValues.join(", "));
          categories.push(key);
          values.push(...selectedValues);
        }
      } else if (prop) {
        eventService.track("Filter - Single", key, prop);
        categories.push(key);
        values.push(prop);
      }
    });
    eventService.track(
      "Filter - Combo",
      categories.join(", "),
      values.join(", ")
    );
  }
}
